import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";

import { AuthBrandHeader } from "@/components/portal/AuthBrandHeader";
import { HomeTopNav } from "@/components/portal/HomeTopNav";
import { usePageTitle } from "@/hooks/usePageTitle";
import { confirmPasswordReset } from "@/lib/api/reset-password";
import {
  getResetPasswordErrorMessage,
  isResetPasswordFormValid,
  RESET_PASSWORD_SUCCESS_MESSAGE,
  shouldOfferResetPasswordRequestLink,
  toResetPasswordRequest,
} from "@/lib/api/reset-password-form";
import { parseApiError } from "@/lib/api/api-error";
import { getAuthSession } from "@/lib/auth/session";
import { PORTAL_HOME_ROUTE } from "@/lib/portal/portal-routes";

export default function ResetPasswordConfirmPage() {
  usePageTitle("Set new password");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token")?.trim() ?? "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  if (getAuthSession()) {
    return <Navigate to={PORTAL_HOME_ROUTE} replace />;
  }

  if (!token) {
    return <Navigate to="/forgot-password" replace />;
  }

  const formValues = { password, confirmPassword };
  const canSubmit = isResetPasswordFormValid(formValues);
  const passwordsMismatch =
    confirmPassword.length > 0 && password !== confirmPassword;

  async function handleSubmit(event?: { preventDefault(): void }) {
    event?.preventDefault();
    if (!canSubmit) {
      return;
    }

    setLoading(true);
    setError(null);
    setErrorCode(undefined);

    try {
      await confirmPasswordReset(toResetPasswordRequest(token, formValues));
      navigate(PORTAL_HOME_ROUTE, {
        replace: true,
        state: { resetSuccess: RESET_PASSWORD_SUCCESS_MESSAGE },
      });
    } catch (err: unknown) {
      const parsed = parseApiError(err);
      setErrorCode(parsed.code);
      setError(getResetPasswordErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <HomeTopNav />
      <main id="main-content" className="auth-shell">
        <div className="auth-layout">
          <AuthBrandHeader testId="home-reset-password-brand-logo" />

          <div className="auth-card" data-testid="home-reset-password-card">
            <div className="auth-hdr">
              <p className="auth-eye">Password reset</p>
              <h1 data-testid="home-reset-password-title">Choose a new password</h1>
              <p>Enter and confirm your new password below.</p>
            </div>
            <div className="auth-body">
              <form
                className="auth-form"
                data-testid="home-reset-password-form"
                noValidate
                onSubmit={(event) => {
                  void handleSubmit(event);
                }}
              >
              {error && (
                <div className="error-msg" data-testid="home-reset-password-error" role="alert">
                  <i className="ti ti-alert-circle" />
                  <span>
                    {error}{" "}
                    {shouldOfferResetPasswordRequestLink(errorCode, error) ? (
                      <Link to="/forgot-password" data-testid="home-reset-password-request-link">
                        Request a new link →
                      </Link>
                    ) : null}
                  </span>
                </div>
              )}

              <div className="field">
                <label htmlFor="home-reset-password">New password</label>
                <div className="pw-input-wrap">
                  <input
                    id="home-reset-password"
                    type={showPw ? "text" : "password"}
                    data-testid="home-reset-password"
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                    }}
                  />
                  <button
                    className="show-pw-btn"
                    data-testid="home-reset-password-toggle"
                    onClick={() => setShowPw((v) => !v)}
                    type="button"
                    aria-label={showPw ? "Hide password" : "Show password"}
                    aria-pressed={showPw}
                  >
                    <i
                      className={`ti ${showPw ? "ti-eye-off" : "ti-eye"}`}
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>

              <div className="field">
                <label htmlFor="home-reset-password-confirm">Confirm password</label>
                <input
                  id="home-reset-password-confirm"
                  type={showPw ? "text" : "password"}
                  data-testid="home-reset-password-confirm"
                  placeholder="Repeat your password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError(null);
                  }}
                />
                {passwordsMismatch && (
                  <p
                    className="field-error"
                    data-testid="home-reset-password-mismatch"
                  >
                    Passwords do not match.
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="submit-btn auth-submit"
                data-testid="home-reset-password-submit"
                disabled={loading || !canSubmit}
              >
                {loading ? (
                  <>
                    <i
                      className="ti ti-loader-2"
                      style={{ animation: "spin 1s linear infinite" }}
                    />{" "}
                    Updating…
                  </>
                ) : (
                  <>
                    <i className="ti ti-lock" /> Update password
                  </>
                )}
              </button>
              </form>
            </div>
          </div>

          <p className="already">
            <Link to={PORTAL_HOME_ROUTE} data-testid="home-reset-password-login-link">
              Back to log in →
            </Link>
          </p>
        </div>
      </main>
    </>
  );
}
